import { useResponsiveValue } from '@superdispatch/ui';
import { useEffect } from 'react';
import {
  FormikDrawerActions,
  FormikDrawerContent,
} from 'shared/form/FormikDrawer';
import { ScreenshotCheckDialog } from './ScreenshotCheckDialog';
import {
  UploadScreenshotTitle,
  UploadTransactionScreenshotFormContent,
  useUploadScreenshotStep,
} from './UploadTransactionScreenshotForm';

interface FormikState {
  isSubmitting: boolean;
  hasUnsavedChanges: boolean;
}

interface UploadScreenshotStepProps {
  onBack: () => void;
  onClose?: () => void;
  onComplete: () => void;
  onVerified?: () => void;
  onFormikStateChange?: (state: FormikState) => void;
}

export function UploadScreenshotStep({
  onBack,
  onClose,
  onComplete,
  onVerified,
  onFormikStateChange,
}: UploadScreenshotStepProps) {
  const isMobile = useResponsiveValue(true, false);
  const {
    formik,
    isCheckingInBackground,
    isCheckDialogOpen,
    openCheckDialog,
    closeCheckDialog,
    handleBack,
    submitLabel,
    isSubmitDisabled,
  } = useUploadScreenshotStep({ onBack, onComplete, onVerified });
  const isSubmitted = formik.status.type === 'submitted';

  useEffect(() => {
    // Leaving during a background check is safe: it keeps running.
    onFormikStateChange?.({
      isSubmitting: formik.isSubmitting,
      hasUnsavedChanges:
        formik.dirty && !isSubmitted && !isCheckingInBackground,
    });
  }, [
    formik.dirty,
    formik.isSubmitting,
    isSubmitted,
    isCheckingInBackground,
    onFormikStateChange,
  ]);

  return (
    <>
      <FormikDrawerContent
        formik={formik}
        title={
          <UploadScreenshotTitle>
            Upload Transaction Screenshot
          </UploadScreenshotTitle>
        }
        enableBack={true}
        onBack={handleBack}
        onClose={onClose}
        onCloseRequest={isCheckingInBackground ? openCheckDialog : undefined}
        disableAutoClose={true}
        disableNavigationBlock={true}
        actions={
          <FormikDrawerActions
            submitButtonLabel={submitLabel}
            submitDisabled={isSubmitDisabled}
            fullWidth={isMobile}
          />
        }
      >
        <UploadTransactionScreenshotFormContent />
      </FormikDrawerContent>

      <ScreenshotCheckDialog
        open={isCheckDialogOpen}
        onKeepWaiting={closeCheckDialog}
        onClose={() => {
          closeCheckDialog();
          onClose?.();
        }}
      />
    </>
  );
}
